import React from 'react';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ServerFilterClauses } from '~commonInfra/utils/ServerFilterClauses';
import { IdentityPoolFilterHelper } from '~src/authentication/helpers/IdentityPoolFilterHelper';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { AdvancedFilterPanel, AdvancedFilterPanelProps } from '~src/spacesPlatform/components/Explorer/AdvancedFilterPanel';
import { IFilterDefinition, ISelectFilterDefinition, ISelectFilterDefinitionOption } from '~src/spacesPlatform/services/FilterPanelService';
import { isGroupSpace } from '~src/spacesPlatform/state/SpaceInfo';

import { SiteRoleFilterHelper } from '../../../../siteManagement/helpers/SiteRoleFilterHelper';
import { IStringValuedFilterClause } from '../../../../spacesPlatform/models/IFilterClause';
import { SiteRolesResource } from '../../../services/resources/SiteRoles';
import { SiteUsers } from '../../../services/resources/SiteUsers';
import { SiteUserDefinition } from '../../contentDefinition/SiteUserDefinition';
import { SiteRoles } from '../../serverApi/types/ISiteRole';


export interface SiteUsersAdvancedFilterPanelProps extends AdvancedFilterPanelProps { }

export interface SiteUsersAdvancedFilterPanelState {
  siteRoleOptionsWithCounts: ISelectFilterDefinitionOption<string>[];
  identityPoolFilterDefinition: ISelectFilterDefinition<string>;
}

// This function is a weird way to update filter definitions asynchronously
// since those definitions are created in the child component - AdvancedFilterPanel.
// See related comment there.
function transformFilterDefinitions(
  siteRoleOptionsWithCounts: ISelectFilterDefinitionOption<string>[],
  identityPoolFilterDefinition: ISelectFilterDefinition<string>
): (filterDefinitions: IFilterDefinition<any>[]) => void {
  return (filterDefinitions: IFilterDefinition<any>[]) => {
    if (siteRoleOptionsWithCounts) {
      (filterDefinitions[0] as ISelectFilterDefinition<string>).options = siteRoleOptionsWithCounts;
    }

    if (identityPoolFilterDefinition) {
      filterDefinitions[1] = identityPoolFilterDefinition;
    }
  };
}

export class SiteUsersAdvancedFilterPanel extends React.Component<SiteUsersAdvancedFilterPanelProps, SiteUsersAdvancedFilterPanelState> {

  private isUnmounted = false;
  private siteRoles: SiteRolesResource = undefined;
  private memoizedTransformFilterDefinitions = defaultMemoize(transformFilterDefinitions);

  public constructor(props: SiteUsersAdvancedFilterPanelProps) {
    super(props);
    this.siteRoles = SiteRolesResource.instance;
    this.state = {
      siteRoleOptionsWithCounts: null,
      identityPoolFilterDefinition: null
    };
  }

  public render() {
    const transformFilterDefinitions = this.memoizedTransformFilterDefinitions(
      this.state.siteRoleOptionsWithCounts,
      this.state.identityPoolFilterDefinition
    );
    return <AdvancedFilterPanel {...this.props} transformFilterDefinitions={transformFilterDefinitions} />;
  }

  public componentDidMount() {
    this.updateIdentityPoolFilterDefinition();
    this.updateSiteRoleCounts();
  }

  public componentDidUpdate(prevProps: SiteUsersAdvancedFilterPanelProps) {
    if (this.shouldUpdateSiteRoleCounts(prevProps)) {
      this.updateSiteRoleCounts();
    }
  }

  public componentWillUnmount() {
    this.isUnmounted = true;
  }


  private updateSiteRoleCounts = async () => {
    let query = SiteUserDefinition.getBasicQuery();
    if (isGroupSpace(this.props.spaceInfo)) {
      query = query.addFilter((ServerFilterClauses.serverFilterClauses.inGroup(this.props.spaceInfo.item.id)));
    }
    if (this.searchTerm(this.props)) {
      query = query.addFilter(this.props.mixedContentFilterMap.search);
    }
    if (this.identityPoolFilterTerm(this.props)) {
      query = query.addFilter(this.props.mixedContentFilterMap.identityPool);
    }
    const userCountsByRole = await SiteUsers.instance.getUserCountsBySiteRole(query);

    // ServerAdministrator is only a valid site role on Tableau Server - the backend never
    // returns it for site users on Tableau Cloud, so it is excluded from the filter there too.
    const siteRolesToExclude = getSessionManagementServiceInstance().isOnlineEnvironment() ?
      [SiteRoles.Guest, SiteRoles.ServerAdministrator] : [SiteRoles.Guest];
    const siteRoleOptionsWithCounts = SiteRoleFilterHelper.siteRoleOptions(
      _.without(this.siteRoles.allOptions(), ...siteRolesToExclude),
      userCountsByRole
    );

    !this.isUnmounted && this.setState({ siteRoleOptionsWithCounts });
  };

  private updateIdentityPoolFilterDefinition = async () => {
    if (FeatureFlags.identityPoolsEnabled) {
      const identityPoolFilterDefinition = await IdentityPoolFilterHelper.identityPoolDefinition();
      this.setState({identityPoolFilterDefinition});
    }
  };

  private shouldUpdateSiteRoleCounts(prevProps: SiteUsersAdvancedFilterPanelProps) {
    return (this.searchTerm(this.props) !== this.searchTerm(prevProps)) ||
    (this.identityPoolFilterTerm(this.props) !== this.identityPoolFilterTerm(prevProps));
  }

  private searchTerm = (props: SiteUsersAdvancedFilterPanelProps) => {
    return (props.mixedContentFilterMap.search
      && (props.mixedContentFilterMap.search as IStringValuedFilterClause).value)
      || '';
  };

  private identityPoolFilterTerm = (props: SiteUsersAdvancedFilterPanelProps) => {
    return (props.mixedContentFilterMap.identityPool
      && (props.mixedContentFilterMap.identityPool as IStringValuedFilterClause).value)
      || '';
  };
}
